import { motion } from 'framer-motion';
import { Github, Globe, Calendar, Clock } from 'lucide-react';
import { Project } from '../../types/project';
import { VisitorCounter } from './VisitorCounter';
import { ProjectDescription } from '../projects/ProjectDescription';

interface ProjectHeaderProps {
  project: Project;
  className?: string;
}

export const ProjectHeader: React.FC<ProjectHeaderProps> = ({
  project,
  className = '',
}) => {
  const containerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        when: 'beforeChildren',
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.4 },
    },
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
  };

  // Rest of the component remains the same until the Description section
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className={`relative ${className}`}
    >
      {/* Previous code remains the same... */}
      <div
        className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl p-4 md:p-6
        shadow-lg shadow-gray-100/20 dark:shadow-gray-900/30
        border border-gray-200/50 dark:border-gray-700/50"
      >
        {/* Header and Technologies sections remain the same... */}
        <div className="flex flex-col gap-4">
          {/* Header Row: Title + Metadata + Actions */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            {/* Title and Metadata */}
            <div className="space-y-2 sm:space-y-0 sm:flex sm:items-center sm:gap-4">
              <motion.h1
                variants={itemVariants}
                className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent 
                  bg-gradient-to-r from-violet-600 via-blue-600 to-cyan-600
                  dark:from-violet-400 dark:via-blue-400 dark:to-cyan-400"
              >
                {project.title}
              </motion.h1>

              {/* Metadata inline */}
              <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                {project.metadata?.createdAt && (
                  <motion.div
                    variants={itemVariants}
                    className="flex items-center gap-1"
                  >
                    <Calendar size={14} />
                    <span>
                      Created {formatDate(project.metadata.createdAt)}
                    </span>
                  </motion.div>
                )}
                {project.metadata?.lastUpdated && (
                  <motion.div
                    variants={itemVariants}
                    className="flex items-center gap-1"
                  >
                    <Clock size={14} />
                    <span>
                      Updated {formatDate(project.metadata.lastUpdated)}
                    </span>
                  </motion.div>
                )}
                <motion.div
                  variants={itemVariants}
                  className="border-l border-gray-300 dark:border-gray-600 pl-3"
                >
                  <VisitorCounter type="project" projectId={project.id} />
                </motion.div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 sm:ml-4">
              {project.githubUrl && (
                <motion.a
                  href={project.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variants={itemVariants}
                  whileHover={{ scale: 1.02 }}
                  className="flex items-center gap-1.5 px-3 py-1.5
                    bg-white/90 dark:bg-gray-700/90 rounded-lg
                    shadow-sm hover:shadow-md border border-gray-200 dark:border-gray-600"
                >
                  <Github size={16} />
                  <span className="text-sm font-medium">Source</span>
                </motion.a>
              )}

              {project.demoUrl && (
                <motion.a
                  href={project.demoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variants={itemVariants}
                  whileHover={{ scale: 1.02 }}
                  className="flex items-center gap-1.5 px-3 py-1.5
                    bg-gradient-to-r from-violet-500 to-blue-500
                    hover:from-violet-600 hover:to-blue-600
                    text-white rounded-lg shadow-sm hover:shadow-md"
                >
                  <Globe size={16} />
                  <span className="text-sm font-medium">Demo</span>
                </motion.a>
              )}
            </div>
          </div>

          {/* Technologies */}
          <motion.div
            variants={itemVariants}
            className="flex flex-wrap gap-1.5"
          >
            {project.technologies.map((tech, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.02 }}
                className={`px-2 py-1 rounded-md text-xs font-medium
                  shadow-sm border ${tech.colorClass}`}
              >
                {tech.name}
              </motion.div>
            ))}
          </motion.div>

          {/* Description */}
          <ProjectDescription project={project} />
        </div>
      </div>
    </motion.div>
  );
};
